import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  ACTIVATE_ACCOUNT,
  CHOOSE_NEW_PASSWORD,
  CREATE_SESSION_URL,
  PASSWORD_FORGOTTEN,
  REGISTER_USER_URL,
  RESET_PASSWORD,
  LOGOUT_URL,
  CHECK_TOKEN_SESSION_URL,
  LOGIN_URL,
  LOCALSTORAGE_TOKEN,
  LOCALSTORAGE_REFRESH_TOKEN,
} from '../utils/cst';
import { Router } from '@angular/router';
import { LangService } from './lang.service';
import { throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { KeycloakService } from 'keycloak-angular';

@Injectable()
export class AuthService {
  public params: HttpParams;
  public userName: string;
  public spinner: NgxSpinnerService;
  public beforeLogOut;

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private langService: LangService,
    private toastService: ToastrService,
    private keycloakService: KeycloakService
  ) {}

  setSpinner(spinner: NgxSpinnerService) {
    this.spinner = spinner;
  }

  // *****************************************************************************************************************
  // REQUESTS FOR AUTHENTICATION
  // *****************************************************************************************************************

  checkTokenSession(token: string, refreshToken: string, lang: string) {
    return this.httpClient
      .post<any>(CHECK_TOKEN_SESSION_URL, { token, refreshToken, lang })
      .pipe(
        tap((sessionResponse) => this.onSessionOpened(sessionResponse, true)),
        catchError((error) => {
          this.toastService.error(error.error);
          return throwError(error);
        })
      );
  }

  login(log: string, pass: string, lang: string) {
    return this.httpClient
      .post<any>(CREATE_SESSION_URL, { login: log, password: pass, lang: lang })
      .pipe(
        tap((sessionResponse) => this.onSessionOpened(sessionResponse, true))
      );
  }

  register(name: string, email: string, password: string, lang: string) {
    return this.httpClient.post<any>(REGISTER_USER_URL, {
      name,
      email,
      password,
      lang,
    });
  }

  passwordForgotten(email: string) {
    return this.httpClient.post<any>(PASSWORD_FORGOTTEN, email);
  }

  chooseNewPassword(passwordToken: string, newPassword: string) {
    return this.httpClient.put<any>(CHOOSE_NEW_PASSWORD, newPassword, {
      params: { tok: passwordToken },
    });
  }

  resetPassword(oldPassword: string, newPassword: string) {
    return this.httpClient.put<any>(RESET_PASSWORD, {
      currentPassword: oldPassword,
      newPassword,
    });
  }

  activateAccount(tok: string) {
    return this.httpClient.get<any>(ACTIVATE_ACCOUNT, { params: { tok } });
  }

  logOut() {
    try {
      // Observed a bug on a deployed environment where this call failed and prevented the rest of the method from executing
      // Add a try-catch because no matter what happens, we should not prevent logout
      this.beforeLogOut?.ngOnDestroy();
    } catch (e) {
      console.error(e);
    }
    return this.httpClient.get(LOGOUT_URL, {}).subscribe(() => {
      localStorage.clear();
      this.keycloakService.logout(LOGIN_URL);
    });
  }

  // *****************************************************************************************************************
  // PRIVATE METHODS
  // *****************************************************************************************************************

  private onSessionOpened(
    sessionResponse: {
      jwt: string;
      refreshToken: string;
      name: string;
      lang: string;
    },
    isRelogin: boolean
  ): void {
    if (sessionResponse.jwt !== null) {
      if (!isRelogin) {
        localStorage.clear();
      }
      localStorage.setItem(LOCALSTORAGE_TOKEN, sessionResponse.jwt);
      this.langService.update(sessionResponse.lang, false);
    }
    if (sessionResponse.refreshToken) {
      localStorage.setItem(
        LOCALSTORAGE_REFRESH_TOKEN,
        sessionResponse.refreshToken
      );
    }
  }
}
